/**
 * Fill-ups: the only fuel figure that is not an estimate.
 *
 * Recording what went into the tank does three things. It starts the
 * since-fill-up span, so the trip computer can show consumption the way a car
 * shows it between fills. It gives a real L/100km from the pump over every
 * full-to-full span. And it calibrates the app's own fuel estimate, which is
 * otherwise only as good as the sensor it is derived from.
 */
import { useState } from 'react';
import {
  fuelSpend, lifetimeConsumption, type FuelFill, type ObdSession, type TankSpan, type VehicleProfile,
} from '@obd/core';
import type { CarRecordsApi } from '../store/records';
import { correctionText } from '../store/carProfile';

export function FuelCard({
  records, session, vehicle,
}: {
  records: CarRecordsApi;
  session: ObdSession;
  vehicle: VehicleProfile;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const tank = session.summary('tank');
  const lastFill = records.fills[records.fills.length - 1];
  const spend = fuelSpend(records.fills);
  const real = lifetimeConsumption(records.fills);
  const cal = records.calibration;

  const record = async (input: FillFormValues): Promise<void> => {
    const fill = await records.addFill({
      litres: input.litres,
      full: input.full,
      ...(input.pricePerL !== undefined ? { pricePerL: input.pricePerL } : {}),
      ...(input.odometerKm !== undefined ? { odometerKm: input.odometerKm } : {}),
      ...(input.t !== undefined ? { t: input.t } : {}),
    });
    if (!fill) {
      setMessage('That fill-up could not be recorded. Check the litres.');
      return;
    }
    setOpen(false);
    setMessage(
      'Recorded ' + fill.litres.toFixed(2) + ' L'
      + (fill.distanceKm > 1 ? ' after ' + fill.distanceKm.toFixed(1) + ' km' : '')
      + '. The “since fill-up” figures start again now.',
    );
  };

  return (
    <div className="card fuel-card">
      <h3>Fuel</h3>
      <p className="muted small">
        Tell the app when you fill up and it can do what your car does: show consumption since the
        last fill-up, and compare its own estimate with what the pump actually delivered.
      </p>

      {records.locked && (
        <p className="warn-card">
          The simulator is running. Stop it before recording a fill-up, so the figures stay the real
          car’s.
        </p>
      )}
      {message && <p className="small profile-note" role="status">{message}</p>}

      <h4 className="subhead">Since the last fill-up</h4>
      <div className="kv-grid">
        <KV label="Distance" value={tank.distanceKm.toFixed(1) + ' km'} />
        <KV label="Fuel used (estimated)" value={tank.fuelL.toFixed(2) + ' L'} />
        <KV
          label="Consumption (estimated)"
          value={tank.consumption === undefined ? '—' : tank.consumption.toFixed(1) + ' L/100km'}
        />
        {/* Only after a brim: from a part fill nobody knows what was in there. */}
        <KV
          label="Left in tank (from the last brim)"
          value={
            lastFill?.full && vehicle.tankCapacityL > 0
              ? Math.max(0, vehicle.tankCapacityL - tank.fuelL).toFixed(0) + ' L of ' + vehicle.tankCapacityL + ' L'
              : '—'
          }
        />
      </div>

      <div className="button-row" style={{ marginTop: 10 }}>
        <button
          className="btn"
          data-variant="primary"
          disabled={records.locked}
          onClick={() => {
            setMessage(null);
            setOpen((v) => !v);
          }}
        >
          {open ? 'Cancel' : 'I filled up'}
        </button>
      </div>

      {open && <FillForm onSubmit={(v) => void record(v)} />}

      <h4 className="subhead">What the pump says</h4>
      {cal ? (
        <p className="small">
          Over {cal.spans === 1 ? 'one full-to-full span' : cal.spans + ' full-to-full spans'} and{' '}
          {Math.round(cal.distanceKm)} km, the pump delivered {cal.litres.toFixed(1)} L where the app
          estimated {cal.estimatedL.toFixed(1)} L. Its estimate {correctionText(cal.factor, 'the pump')}, and every
          fuel figure is corrected by that from now on.
        </p>
      ) : (
        <p className="muted small">
          Fill the tank right up twice, at least 50 km apart, and the app will calibrate its fuel
          figures against the pump — the same way GPS calibrates the speed reading.
        </p>
      )}
      {real && (
        <p className="small">
          Measured at the pump over {Math.round(real.distanceKm)} km:{' '}
          <strong>{real.consumption.toFixed(1)} L/100km</strong>.
          {spend && ' Fuel has cost ' + spend.total.toFixed(2) + ' at an average of '
            + spend.perLitre.toFixed(3) + ' per litre.'}
        </p>
      )}

      {records.spans.length > 0 && <SpanTable spans={records.spans} />}

      {records.fills.length > 0 && (
        <>
          <h4 className="subhead">Fill-ups</h4>
          <div className="fill-list">
            {[...records.fills].reverse().slice(0, 12).map((fill) => (
              <FillRow key={fill.id} fill={fill} onRemove={() => void records.removeFill(fill.id)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SpanTable({ spans }: { spans: TankSpan[] }): JSX.Element {
  return (
    <>
      <h4 className="subhead">Tank by tank</h4>
      <div className="span-table">
        <div className="span-row span-head">
          <span>Filled</span>
          <span className="tnum">km</span>
          <span className="tnum">litres</span>
          <span className="tnum">L/100km</span>
          <span className="tnum">vs estimate</span>
        </div>
        {[...spans].reverse().slice(0, 8).map((span) => (
          <div className="span-row" key={span.to.id}>
            <span>{new Date(span.to.t).toLocaleDateString([], { day: 'numeric', month: 'short' })}</span>
            <span className="tnum">{span.distanceKm.toFixed(0)}</span>
            <span className="tnum">{span.litres.toFixed(1)}</span>
            <span className="tnum">{span.consumption.toFixed(1)}</span>
            <span className="tnum muted">
              {span.errorPct === undefined ? '—' : (span.errorPct > 0 ? '+' : '') + span.errorPct.toFixed(1) + '%'}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function FillRow({ fill, onRemove }: { fill: FuelFill; onRemove: () => void }): JSX.Element {
  return (
    <div className="fill-row">
      <span>{new Date(fill.t).toLocaleDateString([], { day: 'numeric', month: 'short', year: '2-digit' })}</span>
      <span className="tnum">{fill.litres.toFixed(2)} L</span>
      <span className="muted small">{fill.full ? 'to the top' : 'part fill'}</span>
      <span className="tnum muted">{fill.distanceKm > 0 ? fill.distanceKm.toFixed(0) + ' km' : '—'}</span>
      <span className="tnum muted">{fill.pricePerL === undefined ? '' : (fill.litres * fill.pricePerL).toFixed(2)}</span>
      <button className="btn" data-size="sm" onClick={onRemove} aria-label="Delete this fill-up">Delete</button>
    </div>
  );
}

interface FillFormValues {
  litres: number;
  full: boolean;
  pricePerL?: number;
  odometerKm?: number;
  t?: number;
}

/** Litres is the only thing that has to be typed; the rest sharpen the picture. */
function FillForm({ onSubmit }: { onSubmit: (values: FillFormValues) => void }): JSX.Element {
  const [litres, setLitres] = useState('');
  const [price, setPrice] = useState('');
  const [odometer, setOdometer] = useState('');
  const [full, setFull] = useState(true);
  const [when, setWhen] = useState(() => localDateTime(new Date()));
  const litresValue = Number(litres.replace(',', '.'));
  const valid = Number.isFinite(litresValue) && litresValue > 0 && litresValue <= 400;

  const submit = (): void => {
    if (!valid) return;
    const priceValue = Number(price.replace(',', '.'));
    const odoValue = Number(odometer.replace(',', '.'));
    const time = new Date(when).getTime();
    onSubmit({
      litres: litresValue,
      full,
      ...(Number.isFinite(priceValue) && priceValue > 0 ? { pricePerL: priceValue } : {}),
      ...(Number.isFinite(odoValue) && odoValue > 0 ? { odometerKm: odoValue } : {}),
      ...(Number.isFinite(time) && time > 0 ? { t: time } : {}),
    });
  };

  return (
    <div className="fill-form">
      <div className="field-row">
        <label className="field">
          <span>Litres</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={litres}
            autoFocus
            placeholder="41.20"
            onChange={(e) => setLitres(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Price per litre</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.001"
            value={price}
            placeholder="optional"
            onChange={(e) => setPrice(e.target.value)}
          />
        </label>
      </div>
      <div className="field-row">
        <label className="field">
          <span>Odometer (km)</span>
          <input
            type="number"
            inputMode="numeric"
            value={odometer}
            placeholder="optional"
            onChange={(e) => setOdometer(e.target.value)}
          />
        </label>
        <label className="field">
          <span>When</span>
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
        </label>
      </div>
      <label className="field field-inline">
        <input type="checkbox" checked={full} onChange={(e) => setFull(e.target.checked)} />
        <span>Filled right to the top</span>
      </label>
      <p className="muted small">
        Only tank-to-tank between two full fills gives a true figure, so leave this ticked whenever
        you brim it. A part fill still counts towards the litres in the span.
      </p>
      <div className="button-row">
        <button className="btn" data-variant="primary" disabled={!valid} onClick={submit}>
          Record fill-up
        </button>
      </div>
    </div>
  );
}

/** `datetime-local` wants the local clock, not an ISO instant in UTC. */
function localDateTime(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
    + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes())
  );
}

function KV({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="kv">
      <span className="muted">{label}</span>
      <span className="tnum">{value}</span>
    </div>
  );
}
